import React, { useRef, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { useLoader } from '../../../loader/LoaderContext';
import userApiService from '../../../services/oraganisation-api.service';
import useUnsavedChanges from '../../../components/UnsavedChanges/useUnsavedChanges';
import { useOutletContext } from 'react-router';
import OrganizationDetails from './OrganizationDetails';

const OrganizationDetailsContainer = () => {
  const { organizationData, refreshOrganizationData, isSuperAdmin } =
    useOutletContext();

  const userAttributes = useSelector((state) => state.auth.userAttributes);
  const userSub = userAttributes?.sub;

  const { showLoader, hideLoader } = useLoader();

  // Form and state refs
  const formikRef = useRef(null);
  const fileInputRef = useRef(null);

  // State management
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(
    organizationData?.org_logo ?? '',
  );

  // Set has unsaved changes whenever form dirty state or selected image changes
  useEffect(() => {
    setHasUnsavedChanges(isFormDirty || selectedImage !== null);
  }, [isFormDirty, selectedImage]);

  // Set preview image when organization data changes
  useEffect(() => {
    if (organizationData?.org_logo && !selectedImage) {
      setPreviewImage(organizationData.org_logo);
    }
  }, [organizationData]);

  // Prepare initial form values from organization data
  const getInitialValues = () => {
    return {
      org_name: organizationData?.org_name ?? '',
      org_practice_type: organizationData?.plan_name ?? '',
      org_phone_number: organizationData?.org_phone_number ?? '',
      org_fax_number: organizationData?.org_fax_number ?? '',
      org_npi_number: organizationData?.org_npi_number ?? '',
      org_street: organizationData?.org_street ?? '',
      org_city: organizationData?.org_city ?? '',
      org_state: organizationData?.org_state ?? '',
      org_zipcode: organizationData?.org_zipcode ?? '',
    };
  };

  // Handle form dirty state change
  const handleFormDirtyChange = (dirty) => {
    setIsFormDirty(dirty);
  };

  // Reset form function to be passed to the hook
  const resetForm = () => {
    if (formikRef.current) {
      formikRef.current.resetForm();
    }

    // Reset image
    setSelectedImage(null);
    setPreviewImage(organizationData?.org_logo ?? '');
    setHasUnsavedChanges(false);
  };

  // Use our custom hook for unsaved changes
  const { showUnsavedModal, handleStayOnPage, handleDiscardChanges } =
    useUnsavedChanges({
      isDirty: hasUnsavedChanges,
      resetForm: resetForm,
    });

  // Handle form submission
  const handleSubmit = async (values) => {
    try {
      showLoader();
      await userApiService.updateOrganisationDetails(
        userSub,
        values,
        userSub,
      );

      // Refresh organization data to get updated information
      hideLoader();

      await refreshOrganizationData();
      setIsFormDirty(false);

      toast.success('Organization details updated successfully');
    } catch (error) {
      const errorMessage = error.response?.body
        ? JSON.parse(error.response.body).error
        : 'Failed to update organization details';
      toast.error(errorMessage);
    } finally {
      hideLoader();
    }
  };

  // Handle image click
  const handleImageClick = () => {
    fileInputRef.current.click();
  };

  // Handle image change — shows preview immediately then uploads to backend
  const handleImageChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setSelectedImage(file);
    setPreviewImage(URL.createObjectURL(file));

    try {
      showLoader();
      const formData = new FormData();
      formData.append('image_type', 'org_logo');
      formData.append('image_data', file);
      formData.append('org_owner_sub', userSub);

      await userApiService.uploadImage(formData);
      await refreshOrganizationData();
      setSelectedImage(null);
      toast.success('Organization image uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      hideLoader();
    }
  };

  return (
    <OrganizationDetails
      initialValues={getInitialValues()}
      handleSubmit={handleSubmit}
      handleFormDirtyChange={handleFormDirtyChange}
      previewImage={previewImage}
      handleImageClick={handleImageClick}
      handleImageChange={handleImageChange}
      fileInputRef={fileInputRef}
      formikRef={formikRef}
      showUnsavedModal={showUnsavedModal}
      handleStayOnPage={handleStayOnPage}
      handleDiscardChanges={handleDiscardChanges}
    />
  );
};

export default OrganizationDetailsContainer;
